import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface VolcesSeedanceProFastParams {
  /**
   * 内容列表，支持文本到视频或图像到视频（首帧）
   */
  content: Array<{
    /**
     * 内容类型，如text或image_url
     */
    type?: string;
    /**
     * 文本内容
     */
    text?: string;
    image_url?: {
      /**
       * 图像URL
       */
      url?: string;
    };
  }>;
  /**
   * 视频分辨率，必须是支持的分辨率之一，默认480p
   * @default "480p"
   * @values 480p | 720p | 1080p
   */
  resolution?: '480p' | '720p' | '1080p';
  /**
   * 宽高比参数
   */
  ratio?: string;
  /**
   * 视频时长参数
   */
  duration?: number;
  /**
   * 帧率参数
   */
  framespersecond?: number;
  /**
   * 是否添加水印
   */
  watermark?: boolean;
  /**
   * 随机种子，-1表示随机
   */
  seed?: number;
  /**
   * 是否固定摄像机
   */
  camerafixed?: boolean;
  /**
   * 是否返回最后一帧
   * @default false
   */
  return_last_frame?: boolean;
}

export interface VolcesSeedanceProFastResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_seedance_pro_fast
 *
 * <p>Volces SeeDance Pro Fast Video Generator</p>
 * <p>Video generation implementation for Volces SeeDance Pro Fast (火山引擎SeeDance Pro Fast).<br>
 * Supports two modes:</p>
 * <ol>
 * <li>Text-to-video: Generate video from text prompt</li>
 * <li>Image-to-video (first frame): Generate video from first frame image + optional text prompt</li>
 * </ol>
 * <p>API Documentation: https://www.volcengine.com/docs/82379/1520757</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesSeedanceProFast } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesSeedanceProFast({
 *   // 参数见 VolcesSeedanceProFastParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesSeedanceProFastParams 接口定义
 * @returns 生成的资源列表
 */
export async function volcesSeedanceProFast(
  params: VolcesSeedanceProFastParams,
): Promise<VolcesSeedanceProFastResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_seedance_pro_fast',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesSeedanceProFastResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
